/**
 * Image optimization — the canonical URL builder for every Supabase-hosted
 * image rendered in Mora.
 *
 * Architecture (Path C — hybrid Supabase render → Vercel edge proxy):
 *
 *   1. Rewrite Supabase /storage/v1/object/public/... → /storage/v1/render/image/public/...
 *      with width/height/format=webp/quality params. Origin payload
 *      drops from 2.6 MB PNG to ~12 KB WebP for a 400² thumbnail (221×).
 *   2. In production, wrap the resulting URL in /_vercel/image so Vercel's
 *      global edge fronts Supabase, auto-negotiates AVIF/WebP per Accept
 *      header, and absorbs cache-miss roundtrips.
 *   3. In dev (`npm run dev`, no Vercel runtime), step 2 is skipped — the
 *      Supabase transform URL is returned directly so the dev server works.
 *
 * Live measurements (project apegsjpotzvxumdedwty, generated-content bucket):
 *   Original PNG @ 2048²        → 2,751,173 bytes
 *   Supabase 400² WebP          →    12,478 bytes  (221× smaller)
 *   Supabase 200² WebP          →     4,834 bytes  (569× smaller)
 *
 * Audit + rationale: tasks/2026-05-02-library-image-perf-analysis.md
 */

/**
 * Width tiers — discrete values keep Vercel's transformation count bounded
 * (Vercel charges per-distinct-(width,quality,url) key). 64 covers list
 * thumbnails, 256/400 cover the 2-col mobile grid, 512/640 cover the 4-col
 * desktop grid + small inspector view, 1024 covers the inspector full-view.
 *
 * If you need a value not in this set, ADD it here. Don't pass arbitrary
 * widths — they fragment the cache.
 */
export type OptimizedWidth = 64 | 128 | 256 | 400 | 512 | 640 | 1024 | 1536;

export interface OptimizeOptions {
  /** Target render width in CSS pixels (the @1× width). */
  width: OptimizedWidth;
  /** JPEG/WebP quality 1-100. Default 75 (Vercel default) for thumbnails. */
  quality?: number;
  /**
   * Resize behavior. `cover` crops to fill (default — square thumbnails).
   * `contain` letterboxes. Only honored by Supabase render; Vercel's proxy
   * always respects the source aspect ratio.
   */
  resize?: 'cover' | 'contain';
  /**
   * Render at 2× the requested width for retina displays. Increases payload
   * but dramatically improves clarity on devicePixelRatio>=2 screens. The
   * srcset builder uses this internally; you don't usually call it.
   */
  retina?: boolean;
  /**
   * When true, request a tiny blurred preview (LQIP). 32px wide, blur=15,
   * quality=60. Used as the `<img>` background while the real image loads.
   * q=60 is the lowest value in this project's vercel.json qualities allowlist
   * [60, 75, 85, 90] — do not lower it or Vercel will return HTTP 400.
   */
  lqip?: boolean;
}

const SUPABASE_OBJECT_PATH = '/storage/v1/object/public/';
const SUPABASE_RENDER_PATH = '/storage/v1/render/image/public/';

/**
 * True when the URL points at a Supabase Storage public object that the
 * project's render endpoint can transform. Returns false for AI-generated
 * URLs hosted elsewhere (e.g. Imagen output direct URLs that bypass Storage).
 */
function isSupabaseStorageUrl(url: string): boolean {
  return url.includes(SUPABASE_OBJECT_PATH) || url.includes(SUPABASE_RENDER_PATH);
}

/**
 * Build a Supabase render-image URL. Idempotent — if the URL is already a
 * render URL, the new params replace the old.
 */
function buildSupabaseTransform(rawUrl: string, opts: OptimizeOptions): string {
  // Strip existing query, then rewrite path.
  const [base] = rawUrl.split('?');
  const path = base.replace(SUPABASE_OBJECT_PATH, SUPABASE_RENDER_PATH);
  const w = opts.lqip ? 32 : opts.retina ? opts.width * 2 : opts.width;
  const params = new URLSearchParams({
    width: String(w),
    height: String(w),
    resize: opts.resize ?? 'cover',
    quality: String(opts.lqip ? 60 : (opts.quality ?? 75)),
    format: 'webp',
  });
  if (opts.lqip) {
    params.set('blur', '15');
  }
  return `${path}?${params.toString()}`;
}

/**
 * Wrap a URL in /_vercel/image for global edge caching + AVIF negotiation.
 * Only safe in production — the endpoint 404s under `npm run dev`.
 */
/**
 * Mirrors `images.sizes` / `images.qualities` in vercel.json. Vercel answers
 * any other value with HTTP 400 (INVALID_IMAGE_OPTIMIZE_REQUEST), which the
 * `<img>` turns into an empty media slot. Guarded against drift by
 * `src/utils/__tests__/image-optimization.vercel-allowlist.test.ts`.
 */
export const VERCEL_IMAGE_SIZES = [
  32, 64, 128, 256, 384, 400, 512, 640, 768, 960, 1024, 1200, 1536, 1600,
] as const;
export const VERCEL_IMAGE_QUALITIES = [60, 75, 85, 90] as const;

function snapUp(value: number, allowed: readonly number[]): number {
  return allowed.find((a) => a >= value) ?? allowed[allowed.length - 1];
}

function snapNearest(value: number, allowed: readonly number[]): number {
  return allowed.reduce((best, a) => (Math.abs(a - value) < Math.abs(best - value) ? a : best));
}

function wrapVercelImage(url: string, width: number, quality: number): string {
  const w = snapUp(width, VERCEL_IMAGE_SIZES);
  const q = snapNearest(quality, VERCEL_IMAGE_QUALITIES);
  return `/_vercel/image?url=${encodeURIComponent(url)}&w=${w}&q=${q}`;
}

/**
 * Get an optimized image URL for the given target width. Hybrid Path C:
 * Supabase render at the data layer, Vercel proxy in prod. In dev returns
 * the Supabase transform URL directly so vite dev server works.
 *
 * Non-Supabase URLs (data: URIs, third-party CDNs, blob: previews) pass
 * through unchanged.
 */
export function getOptimizedImageUrl(rawUrl: string, opts: OptimizeOptions): string {
  if (!rawUrl) return rawUrl;
  if (rawUrl.startsWith('data:') || rawUrl.startsWith('blob:')) return rawUrl;
  if (!rawUrl.startsWith('http')) return rawUrl;

  // Step 1 — Supabase native transform, when possible. Smaller origin payload.
  const supabaseTransformed = isSupabaseStorageUrl(rawUrl)
    ? buildSupabaseTransform(rawUrl, opts)
    : rawUrl;

  // Step 2 — Vercel edge proxy, prod only. AVIF negotiation, global edge cache.
  const isProd = typeof import.meta !== 'undefined' && import.meta.env?.PROD === true;
  if (!isProd) return supabaseTransformed;

  const w = opts.lqip ? 32 : opts.retina ? opts.width * 2 : opts.width;
  const q = opts.lqip ? 60 : (opts.quality ?? 75);
  return wrapVercelImage(supabaseTransformed, w, q);
}

/**
 * Build a srcset string for responsive images. Generates 1× and 2× variants
 * at the given target width, letting the browser pick the right one based on
 * the device's pixelDensity.
 *
 * Usage:
 *   <img
 *     src={getOptimizedImageUrl(url, { width: 400 })}
 *     srcSet={getOptimizedSrcSet(url, 400)}
 *     sizes="(max-width: 768px) 50vw, 240px"
 *   />
 */
export function getOptimizedSrcSet(
  rawUrl: string,
  baseWidth: OptimizedWidth,
  resize?: OptimizeOptions['resize'],
): string {
  if (!rawUrl || rawUrl.startsWith('data:') || rawUrl.startsWith('blob:')) return '';
  // Find the next-tier-up width for the 2× variant.
  const tiers: OptimizedWidth[] = [64, 128, 256, 400, 512, 640, 1024, 1536];
  const upper = tiers.find((t) => t >= baseWidth * 2) ?? 1536;
  const x1 = getOptimizedImageUrl(rawUrl, { width: baseWidth, resize });
  const x2 = getOptimizedImageUrl(rawUrl, { width: upper, resize });
  return `${x1} 1x, ${x2} 2x`;
}

/**
 * Build a low-quality-image-placeholder URL — 32×32, blur=15, quality=20.
 * Roughly 1-2 KB; used as the `<img>` background-image while the network
 * fetch resolves. Crossfade pattern: paint LQIP instantly → fade to real.
 */
export function getLqipUrl(rawUrl: string): string | null {
  if (!rawUrl || rawUrl.startsWith('data:') || rawUrl.startsWith('blob:')) return null;
  if (!isSupabaseStorageUrl(rawUrl)) return null;
  return getOptimizedImageUrl(rawUrl, { width: 64, lqip: true });
}
